/**
 * The wait's arithmetic (v1 ticket 41), DOM-free so a unit test can read it: the bars of a recording's
 * waveform from its samples, and a typed note cut into the lines it is inked in. The element that draws
 * them is `wait-moment.ts`.
 */

/** Bars in a recording's waveform. */
export const WAVE_BARS = 56;
/** The shortest bar, as a share of the tallest: silence still shows as a line. */
export const WAVE_FLOOR = 0.06;
/** After this long, the wait adds its one line on busy recordings. */
export const LONG_WAIT_MS = 20_000;
/** The most lines a note is inked in; a longer note's last line ends on an ellipsis. */
export const NOTE_MAX_LINES = 8;

/**
 * A recording's waveform: `bars` heights from 0 to 1, each the loudness (RMS over every channel) of its
 * stretch of the clip, relative to the loudest stretch and lifted by a square root so quiet speech still
 * shows, never below `WAVE_FLOOR`. No samples, or no bars asked for, gives none.
 */
export function peaksOf(channels: readonly Float32Array[], bars = WAVE_BARS): number[] {
  const n = channels.length ? Math.min(...channels.map((c) => c.length)) : 0;
  if (n === 0 || bars <= 0) return [];
  const rms: number[] = [];
  for (let b = 0; b < bars; b++) {
    const from = Math.floor((b * n) / bars);
    const to = Math.min(n, Math.max(from + 1, Math.floor(((b + 1) * n) / bars)));
    if (from >= n) {
      rms.push(0);
      continue;
    }
    // Long stretches are sampled: a few hundred points a bar draw the same shape.
    const step = Math.max(1, Math.floor((to - from) / 512));
    let sum = 0;
    let count = 0;
    for (const c of channels) {
      for (let i = from; i < to; i += step) {
        sum += c[i] * c[i];
        count++;
      }
    }
    rms.push(Math.sqrt(sum / count));
  }
  const loudest = Math.max(...rms);
  return rms.map((r) => {
    const v = loudest > 0 ? WAVE_FLOOR + (1 - WAVE_FLOOR) * Math.sqrt(r / loudest) : WAVE_FLOOR;
    return Math.round(v * 1000) / 1000;
  });
}

/**
 * A typed note as it is inked: its own line breaks, then a line per sentence, then words wrapped at about
 * `width` characters (a longer word keeps its line). Blank lines go. At most `max` lines, the last ending on
 * an ellipsis when the note runs on.
 */
export function noteLines(text: string, width = 36, max = NOTE_MAX_LINES): string[] {
  const out: string[] = [];
  for (const para of text.split(/\r?\n/)) {
    for (const sentence of para.split(/(?<=[.!?؟…])\s+/)) {
      let line = '';
      for (const word of sentence.split(/\s+/).filter(Boolean)) {
        if (line && line.length + 1 + word.length > width) {
          out.push(line);
          line = word;
        } else {
          line = line ? `${line} ${word}` : word;
        }
      }
      if (line) out.push(line);
    }
  }
  if (out.length <= max) return out;
  const kept = out.slice(0, max);
  kept[max - 1] = `${kept[max - 1].replace(/[\s.…]+$/, '')}…`;
  return kept;
}

/** Whether a line is inked from the right: its first letter is in a right-to-left script. */
export function inkFromRight(line: string): boolean {
  const first = /\p{L}/u.exec(line);
  return !!first && /[\p{Script=Arabic}\p{Script=Hebrew}]/u.test(first[0]);
}

/** When each of `n` lines starts inking (ms): one after another, all begun within `total`. */
export function inkDelays(n: number, step = 900, total = 6000): number[] {
  const gap = n > 1 ? Math.min(step, total / (n - 1)) : 0;
  return Array.from({ length: n }, (_, i) => Math.round(i * gap));
}

/** How long one line of the note takes to ink (the transition in `wait-moment.ts`). */
export const INK_MS = 900;
/** The pause between one pre-drawn turn (a scene's bubble, a transcript's line) and the next. */
export const TURN_STEP_MS = 1400;
/** Who speaks each bubble of the scene pre-drawn under a typed note: the other person, then the learner, in turn. */
export const SCENE_TURNS = ['other', 'you', 'other', 'you'] as const;
/** Who speaks each skeleton line under a recording's waveform: two voices, in turn. */
export const TRANSCRIPT_TURNS = ['a', 'b', 'a', 'b', 'a'] as const;
/** The most characters of the note quoted, faintly, in the learner's bubble. */
export const QUOTE_MAX = 28;

/** When the last of `n` inked lines has finished inking (ms after the first starts); 0 for none. */
export function inkEnd(n: number): number {
  const d = inkDelays(n);
  return d.length ? d[d.length - 1] + INK_MS : 0;
}

/**
 * When each of `n` pre-drawn turns appears (ms): the first at `start`, then one every `step`. They only name the
 * shape of what is coming, so they appear on a clock of their own, never as progress.
 */
export function turnDelays(n: number, start: number, step = TURN_STEP_MS): number[] {
  return Array.from({ length: Math.max(0, n) }, (_, i) => Math.round(Math.max(0, start) + i * step));
}

/**
 * The start of a note, quoted in the learner's bubble: its first line up to `max` characters, cut at a word
 * where one ends near the cut, with an ellipsis when anything was left out. A blank note quotes nothing.
 */
export function noteQuote(text: string, max = QUOTE_MAX): string {
  const all = text.replace(/\s+/g, ' ').trim();
  if (!all) return '';
  if (all.length <= max) return all;
  // One character more, so a word ending right at the cut is kept whole.
  const space = all.slice(0, max + 1).lastIndexOf(' ');
  const head = (space >= max / 2 ? all.slice(0, space) : all.slice(0, max)).replace(/[\s.,;:!?،؛…]+$/u, '');
  return `${head}…`;
}
